import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button, Grid, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import PageContainer from '@/components/ui/containers/PageContainer'

import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import { OrderStatus } from '@/core/models/order.model'
import { useLazyGetMeetingLinksQuery } from '@/core/services/meeting-link'
import { useGetMeetingLinkOrdersQuery } from '@/core/services/order'

import { useIntro } from '@/hooks/useIntro'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { ViewMeetingLinkIntro } from '@/utils/onboarding/intro'

import MeetingLinksCardView, { MeetingLinkData } from './components/MeetingLinksCardView'
import ShareMeetingLinkDialog from './components/ShareMeetingLinkDialog'

export default function MeetingLinksPage() {
  const { t } = useTranslation()

  useIntro(ViewMeetingLinkIntro)

  const authUser = useAppSelector(selectAuthUser)

  const { data: pendingOrders } = useGetMeetingLinkOrdersQuery(
    { acceptedBy: authUser?._id, status: [OrderStatus.Pending] },
    { skip: !authUser }
  )
  const [getMeetingLinks, { data: meetingLinks, isLoading }] = useLazyGetMeetingLinksQuery()

  const [shareDialog, setShareDialog] = useState<{ open: boolean; name: string; slug: string }>({
    open: false,
    name: '',
    slug: '',
  })

  // Group pending orders by meeting link
  const pendingOrdersByMeetingLink = useMemo(() => {
    const map: Record<string, number> = {}
    pendingOrders?.forEach((order) => {
      const mlId = order.receiverId
      if (mlId) {
        map[mlId] = (map[mlId] || 0) + 1
      }
    })
    return map
  }, [pendingOrders])

  const meetingLinksData = useMemo((): MeetingLinkData[] => {
    return (
      meetingLinks?.map((meetingLink) => {
        const meetingPlaceTypes = _.uniq(
          meetingLink.availabilities?.map((a) => a.meetingPlace).filter(Boolean) ?? []
        ) as AvailabilityMeetingPlace[]

        return {
          id: meetingLink._id,
          name: meetingLink.name,
          slug: meetingLink.slug,
          specialities: _.map(meetingLink.specialities, 'name'),
          staffCount: meetingLink.staffs?.length ?? 0,
          meetingPlaceTypes,
          pendingOrdersCount: pendingOrdersByMeetingLink[meetingLink._id] || 0,
        }
      }) ?? []
    )
  }, [meetingLinks, pendingOrdersByMeetingLink])

  useEffect(() => {
    if (!authUser) return
    getMeetingLinks({ staffs: [authUser._id] })
  }, [authUser, getMeetingLinks])

  const handleShare = (meetingLink: { id: string; name: string; slug: string }) => {
    setShareDialog({ open: true, name: meetingLink.name, slug: meetingLink.slug })
  }

  return (
    <PageContainer>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item xs={12} sm={8}>
          <Typography variant="h2">{t('page.meetingLinks.title')}</Typography>
        </Grid>
        <Grid item xs={12} sm={4} container justifyContent="flex-end">
          <Button component={Link} disableElevation startIcon={<AddIcon htmlColor="#FFF" />} to="/meeting-links/create" variant="contained">
            {t('page.meetingLinks.createMeetingButton.label')}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <div id="meeting-link-list">
            <MeetingLinksCardView data={meetingLinksData} loading={isLoading} onShare={handleShare} />
          </div>
        </Grid>
      </Grid>

      <ShareMeetingLinkDialog
        open={shareDialog.open}
        onClose={() => setShareDialog({ open: false, name: '', slug: '' })}
        meetingLink={{ name: shareDialog.name, slug: shareDialog.slug }}
      />
    </PageContainer>
  )
}
